import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../../config/axiosConfig';
import { INVITE_URLS, TIMEZONES, API_URLS, SIGNUP_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import TermsAndConditionsModal from '../../components/modals/TermsAndConditionsModal';
import PrivacyPolicyModal from '../../components/modals/PrivacyPolicyModal';

const useQuery = () => new URLSearchParams(useLocation().search);

const createAcceptInviteSchema = (requireUploadLimitAck: boolean) =>
  z
    .object({
      firstName: z.string().min(1, 'First name is required').trim(),
      lastName: z.string().min(1, 'Last name is required').trim(),
      phone: z.string().min(1, 'Phone number is required').trim(),
      timezone: z.string().min(1, 'Timezone is required'),
      password: z.string().min(6, 'Password must be at least 6 characters'),
      confirmPassword: z.string().min(6, 'Password confirmation is required'),
      acknowledgeUploadLimit: z.boolean().optional(),
      acceptTerms: z.boolean().refine((val) => val === true, {
        message: 'You must accept the Terms & Conditions',
      }),
    })
    .superRefine((data, ctx) => {
      if (requireUploadLimitAck && data.acknowledgeUploadLimit !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['acknowledgeUploadLimit'],
          message: 'You must acknowledge the game upload and subscription terms',
        });
      }
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    });

type AcceptInviteFormData = z.infer<ReturnType<typeof createAcceptInviteSchema>>;

interface AcceptInviteProps {
  setIsAuthenticated: (auth: boolean) => void;
}

const AcceptInvite: React.FC<AcceptInviteProps> = ({ setIsAuthenticated }) => {
  const query = useQuery();
  const navigate = useNavigate();
  const token = query.get('token') || '';
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [client, setClient] = useState<any>(null);
  const [isEmployee, setIsEmployee] = useState<boolean>(false);
  const [employeeRole, setEmployeeRole] = useState<any>(null);
  const [requiresPaymentAcknowledgment, setRequiresPaymentAcknowledgment] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState<boolean>(false);
  const [billingEnabled, setBillingEnabled] = useState(true);
  const showUploadLimitAcknowledgment = billingEnabled && requiresPaymentAcknowledgment;
  const acceptInviteSchema = useMemo(
    () => createAcceptInviteSchema(showUploadLimitAcknowledgment),
    [showUploadLimitAcknowledgment],
  );
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<AcceptInviteFormData>({
    resolver: zodResolver(acceptInviteSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      timezone: 'eastern',
      password: '',
      confirmPassword: '',
      acknowledgeUploadLimit: false,
      acceptTerms: false,
    },
  });

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await api.get(`${INVITE_URLS.VALIDATE}?token=${encodeURIComponent(token)}`);
        if (res.data.success) {
          setEmail(res.data.data?.email || '');
          setValue('firstName', res.data.data?.firstName || '');
          setValue('lastName', res.data.data?.lastName || '');
          setValue('phone', res.data.data?.phone || '');
          setValue('timezone', res.data.data?.timezone || 'eastern');
          setClient(res.data.data?.client || null);
          setIsEmployee(res.data.data?.isEmployee || false);
          setEmployeeRole(res.data.data?.role || null);
          setRequiresPaymentAcknowledgment(!!res.data.data?.requiresPaymentAcknowledgment);
          setLoading(false);
        } else {
          showErrorMsg(res.data.message || 'Invalid invitation');
          navigate('/login');
        }
      } catch (e: any) {
        showErrorMsg(e.response?.data?.message || 'Failed to validate invitation');
        navigate('/login');
      }
    };
    if (!token) {
      showErrorMsg('Missing invitation token');
      navigate('/login');
      return;
    }
    validate();
  }, [token, navigate, setValue]);

  useEffect(() => {
    const fetchSignupConfig = async () => {
      try {
        const res = await api.get(SIGNUP_URLS.CONFIG);
        setBillingEnabled(!!res.data?.data?.billingEnabled);
      } catch (error) {
        console.error('Failed to fetch signup config:', error);
        setBillingEnabled(true);
      }
    };

    fetchSignupConfig();
  }, []);

  const onSubmit = async (data: AcceptInviteFormData) => {
    try {
      // First, accept the invitation
      const acceptRes = await api.post(INVITE_URLS.ACCEPT, {
        token,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        timezone: data.timezone,
        password: data.password,
        acceptTerms: true,
      });

      if (acceptRes.data.success) {
        // Now automatically log the user in
        try {
          const loginRes = await api.post(API_URLS.LOGIN, {
            email,
            password: data.password,
          });

          if (loginRes.data.success) {
            const payload: any = loginRes.data.data ? loginRes.data.data : loginRes.data;
            const loginUser = payload.user || {};
            const accessToken = payload.token;
            const refreshToken = payload.refreshToken;

            if (!accessToken) {
              showErrorMsg('Login failed: no token received');
              navigate('/login');
              return;
            }

            // Store the session data
            sessionManager.saveSession({
              token: accessToken,
              refreshToken,
              user: loginUser,
              clientId: loginUser.clientId,
              clientName: payload.client?.name,
              isAuthenticated: true,
              lastValidated: Date.now(),
            });

            // Update the app-level authentication state
            setIsAuthenticated(true);

            showSuccessMsg('Welcome! Your account has been activated successfully.');

            // Redirect logic based on invite type and user roles
            const user = loginUser;
            const userRole = user.role;

            // Check if user is a coach by looking at role value
            const isCoach = userRole?.value === 'headCoach' || userRole?.value === 'assistantCoach';

            // Redirect coaches to Players page to start the setup workflow
            if (user.clientId && isCoach) {
              navigate('/players?welcome=true', { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          } else {
            showSuccessMsg('Invitation accepted successfully. Please log in to continue.');
            navigate('/login');
          }
        } catch (loginError: any) {
          showSuccessMsg('Invitation accepted successfully. Please log in to continue.');
          navigate('/login');
        }
      } else {
        showErrorMsg(acceptRes.data.message || 'Failed to accept invitation');
      }
    } catch (e: any) {
      showErrorMsg(e.response?.data?.message || 'Failed to accept invitation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-gray-600">Validating invitation...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-2xl bg-white border rounded-lg shadow-lg overflow-hidden">
        {/* Header with StatDog Logo */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/images/logo-notext.png"
              alt="StatDog Logo"
              className="h-20 w-20 object-contain bg-white rounded-lg"
            />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Welcome to Statdog</h1>
          {isEmployee && employeeRole ? (
            <div className="bg-white bg-opacity-20 rounded-lg p-3 mb-2">
              <p className="text-green-100 text-sm font-medium">Employee Invitation</p>
              <p className="text-white text-lg font-bold">{employeeRole.label}</p>
            </div>
          ) : null}
          <p className="text-green-100">Complete your account setup to get started</p>
        </div>

        <div className="p-6">
          {/* Invitation Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Invitation Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Invited Email:</span>
                <p className="font-medium text-gray-800">{email}</p>
              </div>
              <div>
                <span className="text-gray-600">{isEmployee ? 'Company:' : 'Organization:'}</span>
                <p className="font-medium text-gray-800">{client?.name || 'STATDOG'}</p>
              </div>
              {isEmployee && employeeRole && (
                <div className="md:col-span-2">
                  <span className="text-gray-600">Employee Role:</span>
                  <div className="flex items-center mt-1">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      {employeeRole.label}
                    </span>
                  </div>
                  <p className="text-gray-600 text-xs mt-1">
                    You are being invited as a STATDOG employee with access to internal systems and resources.
                  </p>
                </div>
              )}
              {!isEmployee && client?.address && (
                <div className="md:col-span-2">
                  <span className="text-gray-600">Address:</span>
                  <p className="font-medium text-gray-800">
                    {client.address}
                    {client.city && `, ${client.city}`}
                    {client.state && `, ${client.state}`}
                    {client.zip && ` ${client.zip}`}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Account Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-gray-700 font-medium mb-1">First Name *</label>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                        errors.firstName ? 'border-red-500' : ''
                      }`}
                    />
                  )}
                />
                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Last Name *</label>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                        errors.lastName ? 'border-red-500' : ''
                      }`}
                    />
                  )}
                />
                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Phone Number *</label>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="tel"
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                        errors.phone ? 'border-red-500' : ''
                      }`}
                    />
                  )}
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Time Zone *</label>
                <Controller
                  name="timezone"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                        errors.timezone ? 'border-red-500' : ''
                      }`}
                    >
                      {TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.timezone && <p className="text-red-500 text-xs mt-1">{errors.timezone.message}</p>}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Password *</label>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="password"
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                        errors.password ? 'border-red-500' : ''
                      }`}
                    />
                  )}
                />
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Confirm Password *</label>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="password"
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                        errors.confirmPassword ? 'border-red-500' : ''
                      }`}
                    />
                  )}
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            {/* Terms and Submit */}
            <div className="border-t pt-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-4">
                {showUploadLimitAcknowledgment && (
                  <>
                    <div className="flex items-start gap-3">
                      <Controller
                        name="acknowledgeUploadLimit"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <input
                            id="upload-limit-invite"
                            type="checkbox"
                            checked={!!value}
                            onChange={onChange}
                            className={`mt-1 h-5 w-5 flex-shrink-0 text-green-600 focus:ring-green-500 rounded border-gray-300 ${
                              errors.acknowledgeUploadLimit ? 'border-red-500' : ''
                            }`}
                          />
                        )}
                      />
                      <label htmlFor="upload-limit-invite" className="text-gray-700 text-sm leading-relaxed">
                        I understand that my free trial allows me to add up to <strong>three games/scrimmages.</strong>{' '}
                        To add more games to STATDOG, I agree to subscribe through the app&apos;s{' '}
                        <strong>Billing &amp; Renewals</strong> page.
                      </label>
                    </div>
                    {errors.acknowledgeUploadLimit && (
                      <p className="text-red-500 text-xs mt-2">{errors.acknowledgeUploadLimit.message}</p>
                    )}
                  </>
                )}

                <div className="flex items-start gap-3">
                  <Controller
                    name="acceptTerms"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <input
                        id="terms"
                        type="checkbox"
                        checked={value}
                        onChange={onChange}
                        className={`mt-1 h-5 w-5 flex-shrink-0 text-green-600 focus:ring-green-500 rounded border-gray-300 ${
                          errors.acceptTerms ? 'border-red-500' : ''
                        }`}
                      />
                    )}
                  />
                  <label htmlFor="terms" className="text-gray-700 text-sm leading-relaxed">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-green-600 hover:text-green-700 underline font-medium"
                    >
                      Terms & Conditions
                    </button>{' '}
                    and{' '}
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="text-green-600 hover:text-green-700 underline font-medium"
                    >
                      Privacy Policy
                    </button>
                    .
                  </label>
                </div>
                {errors.acceptTerms && <p className="text-red-500 text-xs mt-2">{errors.acceptTerms.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Setting up account...' : 'Complete Setup & Sign In'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      <TermsAndConditionsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
    </div>
  );
};

export default AcceptInvite;
